import { Typography, Pagination, useMediaQuery, Box } from "@mui/material";
import {
  mobileWidth,
  formatDateDayjs,
  useToast,
  AutostoreTable
} from "@qubit/autoparts";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { ProductCard } from "~/components/productCard/ProductCardRootWithSideRibbon";
import InventoryAdjustDialog, {
  InventoryToModify
} from "~/features/inventoryAdjustDialog/InventoryAdjustDialog";
import { InventoryHoldModal } from "~/features/inventoryHoldModal/InventoryHoldModal";

import { useNavbar, ViewNameTranslation } from "~/hooks/useNavbar";

import {
  generateLocationNameFromInventorySummary,
  getInventoryDateForString,
  holdTypeCheck,
  HoldType,
  runAsyncSafely
} from "~/lib/helpers";

import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import {
  getInventorySummaries,
  deleteInventoryHold,
  getVariantByVariantId
} from "~/redux/actions/inventory";
import { InventoryHoldReasonCode } from "~/redux/public/inventory.openApi";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectWorkstationId } from "~/redux/selectors/workstationsSelectors";
import { InventorySummaryDto } from "~/types/api";

import { InventoryHoldsToolbar } from "./InventoryHoldsToolbar";
import {
  selectCommonHoldsForSelectedSummary,
  selectSelectedRow,
  selectSelectedSummary,
  setIsInventoryAdjustDialogOpen,
  setIsBinHoldModalOpen,
  setSelectedRow
} from "./inventoryHolds.slice";

type Props = { viewTitle?: ViewNameTranslation };

/** Manual Ops Inventory Holds */
export function InventoryHolds(props: Props) {
  const { viewTitle } = props;
  const { errorToast } = useToast();
  const trackedPageName = useMixpanelPageName();

  const isMobile = useMediaQuery(mobileWidth);
  const { successToast } = useToast();

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setToolbar } = useNavbar({ viewTitle });

  const { inv_inventoryDateLabel } = useAppSelector(selectServerConfig);
  const inventorySummaries = useAppSelector(
    (state) => state.inventory.inventorySummaries
  );
  const inventorySummariesCount = useAppSelector(
    (state) => state.inventory.inventorySummariesCount
  );
  const isInventoryAdjustDialogOpen = useAppSelector(
    (state) => state.inventoryHolds.isInventoryAdjustDialogOpen
  );
  const isBinHoldModalOpen = useAppSelector(
    (state) => state.inventoryHolds.isBinHoldModalOpen
  );
  const loadingInventory = useAppSelector(
    (state) => state.inventory.loadingInventory
  );
  const selectedRow = useAppSelector(selectSelectedRow);
  const selectedVariant = useAppSelector(
    (state) => state.inventory.selectedVariant
  );
  const thisWorkstationId = useAppSelector(selectWorkstationId);
  const selectedSummary = useAppSelector(selectSelectedSummary);
  const commonHolds = useAppSelector(selectCommonHoldsForSelectedSummary);

  const [page, setPage] = useState<number>(1);
  const limit = 5;
  const offset = page === 0 ? 0 : (page - 1) * limit;
  const totalPageCount = inventorySummariesCount
    ? Math.ceil(inventorySummariesCount / limit)
    : 0;

  const handleGetInventorySummaries = async () => {
    dispatch(setSelectedRow(null));
    await dispatch(
      getInventorySummaries({
        binTypesExcluded: "autostore",
        hasHold: true,
        limit,
        offset
      })
    );
  };

  useEffect(() => {
    void handleGetInventorySummaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, page]);

  useEffect(() => {
    if (selectedSummary) {
      void dispatch(getVariantByVariantId(selectedSummary.variantId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSummary]);

  const selectedInventoryToModify: InventoryToModify = selectedSummary
    ? {
        binType: selectedSummary.binType,
        binId: selectedSummary.binId,
        inventoryId: selectedSummary.inventoryId,
        locationName: generateLocationNameFromInventorySummary(selectedSummary),
        count: selectedSummary.count,
        committedCount: selectedSummary.committedCount,
        ...getInventoryDateForString(
          inv_inventoryDateLabel,
          selectedSummary.expiration,
          selectedSummary.manufactureDate
        ),
        lotNumber: selectedSummary.lotNumber,
        variantId: selectedSummary.variantId,
        holds: selectedSummary.holds
      }
    : null;

  useEffect(() => {
    setToolbar(selectedSummary && <InventoryHoldsToolbar />);
  }, [selectedSummary, setToolbar]);

  const handleRemoveFlags = (
    inventoryToRemove: InventoryToModify[] | InventorySummaryDto[],
    holdType?: HoldType
  ) => {
    for (const inv of inventoryToRemove) {
      if (holdType) {
        if (
          inv?.holds &&
          inv.holds.some((hold) => hold.reasonCode === holdType.reasonCode)
        ) {
          if (holdTypeCheck(holdType, errorToast, dayjs(inv.expiration)))
            return;
          void runAsyncSafely(async () => {
            await dispatch(
              deleteInventoryHold(
                inv.inventoryId,
                thisWorkstationId || null,
                holdType.reasonCode
              )
            );
            successToast(t("inventory hold removed"));
            if (isInventoryAdjustDialogOpen) {
              dispatch(setIsInventoryAdjustDialogOpen(false));
            }
            dispatch(setSelectedRow(null));
            dispatch(setIsBinHoldModalOpen(false));
            // calculate the current page after some holds are removed
            if (inventorySummaries.length === 1) {
              // if removing the last row on the page, go back a page
              setPage(page === 0 ? 0 : page - 1);
            } else {
              await handleGetInventorySummaries();
            }
          });
        } else if (
          inv?.holds &&
          !inv.holds.some((hold) => hold.reasonCode === holdType.reasonCode)
        ) {
          errorToast(
            `${t(holdType.reasonCode)} ${t("doesn't exist on inventory record")}`,
            {
              duration: 2000
            }
          );
        }
      }
    }
  };

  const removeHoldCb = (holdType: HoldType | null | undefined) => {
    if (holdType && selectedInventoryToModify) {
      handleRemoveFlags([selectedInventoryToModify], holdType);
    }
  };

  let fourthColumnName = "";

  switch (inv_inventoryDateLabel) {
    case "manufacture":
      fourthColumnName = t("manufacture date");
      break;
    case "expiration":
    default:
      fourthColumnName = t("expiration");
      break;
  }

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        maxWidth: (theme) => theme.breakpoints.values.xl,
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          lg: "1fr 2fr"
        },
        justifyContent: "center",
        rowGap: 2,
        mx: "auto",
        mt: 2,
        px: { xs: 2, sm: 3 }
      }}
    >
      {/* product information */}
      {selectedVariant && selectedSummary ? (
        <ProductCard.Root
          productName={selectedVariant.productName ?? ""}
          imageFileName={selectedVariant.imageFilename}
          tempZone={selectedVariant.temperatureZone}
          sx={{ height: "480px" }}
        >
          <ProductCard.Sku sku={selectedVariant.sku} />
          <ProductCard.ProductCount count={selectedSummary.count} />
        </ProductCard.Root>
      ) : (
        <ProductCard.Loading height={480} />
      )}
      {!loadingInventory && inventorySummaries.length > 0 && (
        <AutostoreTable<InventorySummaryDto>
          widthOfCols={["40%", "10%", "15%", "15%", "20%"]}
          headerVariant="overline"
          bodyVariant="body2"
          headerColNames={[
            t("product"),
            t("quantity"),
            t("bin"),
            isMobile ? null : fourthColumnName,
            t("hold type")
          ].filter((el) => el !== null)}
          rowId={(row: InventorySummaryDto) => row.inventoryId}
          renderColumns={
            [
              (row: InventorySummaryDto) => row.productName,
              (row: InventorySummaryDto) => row.count?.value.toString() || "",
              (row: InventorySummaryDto) =>
                generateLocationNameFromInventorySummary(row),
              isMobile
                ? null
                : (row: InventorySummaryDto) => formatDateDayjs(row.expiration),
              (row: InventorySummaryDto) => {
                return (
                  <Box>
                    {row.holds?.map((hold) => (
                      <Box key={hold.reasonCode}>
                        <Typography>
                          {t(
                            hold.reasonCode.toLowerCase() as InventoryHoldReasonCode
                          )}
                        </Typography>
                      </Box>
                    )) || ""}
                  </Box>
                );
              }
            ].filter((el) => el !== null) as ((
              row: InventorySummaryDto
            ) => string | React.ReactElement)[]
          }
          rowData={inventorySummaries}
          selectedRows={selectedRow ? [selectedRow] : undefined}
          selectRowCallback={(row: InventorySummaryDto) => {
            if (row.inventoryId === selectedRow) {
              mixpanelTrack({
                trackedPageName,
                label: "Unselect Inventory Hold Row",
                type: "Unselect Row",
                eventProperties: {
                  "Inventory ID": row.inventoryId,
                  "Product Name": row.productName,
                  "Autostore Bin Number": row.autostoreBinNumber
                }
              });
              dispatch(setSelectedRow(null));
            } else {
              mixpanelTrack({
                trackedPageName,
                label: "Select Inventory Hold Row",
                type: "Select Row",
                eventProperties: {
                  "Inventory ID": row.inventoryId,
                  "Product Name": row.productName,
                  "Autostore Bin Number": row.autostoreBinNumber
                }
              });
              dispatch(setSelectedRow(row.inventoryId));
            }
          }}
          loading={loadingInventory}
        />
      )}
      {!loadingInventory && !inventorySummaries.length && (
        <Typography variant="h5" align="center" style={{ padding: 10 }}>
          {t("no inventory holds")}
        </Typography>
      )}
      {/* only show pagination for the initial holds list */}
      {totalPageCount > 1 && (
        <Pagination
          count={totalPageCount}
          page={page}
          onChange={(_e, p) => {
            mixpanelTrack({
              trackedPageName,
              label: "Change Page",
              type: "Button Click",
              eventProperties: {
                "New Page": p,
                "Previous Page": page
              }
            });
            setPage(p);
            if (window.scrollTo) window.scrollTo(0, 0);
          }}
          shape="rounded"
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: {
              xs: 1,
              lg: 0
            },
            gridColumn: {
              xs: "1/-1",
              lg: "2/3"
            }
          }}
        />
      )}
      {!!selectedInventoryToModify && (
        <InventoryAdjustDialog
          open={isInventoryAdjustDialogOpen}
          onClose={() => dispatch(setIsInventoryAdjustDialogOpen(false))}
          invToModify={selectedInventoryToModify}
          selectedVariant={selectedVariant || null}
          refreshCb={async () => {
            await handleGetInventorySummaries();
            dispatch(setIsInventoryAdjustDialogOpen(false));
          }}
          canceledReason=""
        />
      )}

      <InventoryHoldModal
        confirmCb={(holdType) => removeHoldCb(holdType)}
        open={isBinHoldModalOpen}
        closeModalCallback={() => dispatch(setIsBinHoldModalOpen(false))}
        maxWidth="sm"
        options={commonHolds}
        confirmButtonText={t("remove hold")}
        confirmActionTypeForTracking="Remove Hold"
        currentHold=""
      />
    </Box>
  );
}
